import { useEffect, useMemo, useRef, useState } from 'react';
import { services } from '../data/services';
import { useTypingPlaceholder } from '../hooks/useTypingPlaceholder';
import { getAttribution } from '../lib/attribution';
import {
  trackFormStart,
  trackFormSubmitError,
  trackFormValidationError,
  trackFormView,
  trackLead,
} from '../lib/analytics';
import { Button, Card, Field, Input, RadioCards, Select, Textarea } from './UI';

const INTENT_OPTIONS = [
  { value: 'fix', label: "Fix a problem — something's broken or annoying" },
  { value: 'service', label: 'Ask about a specific service' },
  { value: 'general', label: 'General enquiry — anything else' },
];
const AREA_OPTIONS = [
  'Website or online store',
  'Email, files or backups',
  'Wi-Fi, printers or hardware',
  "Programs that won't talk to each other",
  'Spreadsheets, data or reports',
  "No idea — it's a mystery",
];
const STAGE_OPTIONS = [
  'Just curious for now',
  'After a quote for something specific',
  'Ready to book a free first look',
];
const GENERAL_OPTIONS = [
  'Pricing and how you charge',
  'Checking if this is something you do',
  'Working together',
  'Something else',
];
const URGENCY_OPTIONS = ["It's on fire", 'This week would be great', 'Whenever suits'];

function initialState() {
  const params = new URLSearchParams(window.location.search);
  const requestedService = params.get('service') || '';
  const requestedIntent = params.get('intent') || (requestedService ? 'service' : '');
  return {
    intent: ['fix', 'service', 'general'].includes(requestedIntent) ? requestedIntent : '',
    area: '',
    service: services.some((item) => item.id === requestedService) ? requestedService : '',
    stage: '',
    topic: '',
    urgency: '',
    name: '',
    reply: '',
    business: '',
    detail: '',
    consent: false,
    'bot-field': '',
  };
}

function validate(values) {
  const errors = {};
  if (!values.intent) errors.intent = 'Choose the closest starting point.';
  if (values.intent === 'fix' && !values.area) errors.area = 'Choose the closest area, even if it is only a guess.';
  if (values.intent === 'service' && !values.service) errors.service = 'Choose the service you are asking about.';
  if (values.intent === 'service' && !values.stage) errors.stage = 'Tell us roughly where you are up to.';
  if (values.intent === 'general' && !values.topic) errors.topic = 'Choose the closest topic.';
  if (!values.urgency) errors.urgency = 'Let us know how soon you need a response.';
  if (!values.name.trim()) errors.name = 'Mind telling us your name?';
  const reply = values.reply.trim();
  if (!reply) errors.reply = 'We need somewhere to send the reply.';
  else if (reply.includes('@') && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(reply)) errors.reply = 'That email does not look right — mind checking it?';
  else if (!reply.includes('@') && reply.replace(/\D/g, '').length < 8) errors.reply = 'That phone number looks a little short.';
  if (!values.detail.trim()) errors.detail = 'Tell us a little about it — one sentence is plenty.';
  if (!values.consent) errors.consent = 'Please confirm we can use these details to respond.';
  return errors;
}

export function InquiryForm({ compact = false }) {
  const [values, setValues] = useState(initialState);
  const [errors, setErrors] = useState({});
  const [status, setStatus] = useState('idle');
  const [submitError, setSubmitError] = useState('');
  const [submissionId] = useState(() => crypto.randomUUID());
  const formRef = useRef(null);
  const formStartedRef = useRef(false);
  const formLocation = compact ? 'home' : 'start';
  const typedProblemPlaceholder = useTypingPlaceholder(values.intent === 'fix' && !values.detail);

  const detailCopy = useMemo(() => {
    if (values.intent === 'service') return { label: 'What are you hoping to improve?', placeholder: 'A sentence about your setup or goal helps me come prepared.' };
    if (values.intent === 'general') return { label: "What's on your mind?", placeholder: 'A sentence or two is plenty.' };
    return { label: "What's the problem?", placeholder: typedProblemPlaceholder };
  }, [typedProblemPlaceholder, values.intent]);

  const routeAnswered = (
    (values.intent === 'fix' && values.area)
    || (values.intent === 'service' && values.service && values.stage)
    || (values.intent === 'general' && values.topic)
  );
  const contactAnswered = values.name.trim() && values.reply.trim();

  useEffect(() => {
    const form = formRef.current;
    if (!form) return undefined;

    const recordView = () => trackFormView({ form_location: formLocation });
    if (!('IntersectionObserver' in window)) {
      recordView();
      return undefined;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting) return;
        recordView();
        observer.disconnect();
      },
      { threshold: 0.15 },
    );
    observer.observe(form);
    return () => observer.disconnect();
  }, [formLocation]);

  const recordFormStart = () => {
    if (formStartedRef.current) return;
    formStartedRef.current = true;
    trackFormStart({ form_location: formLocation });
  };

  const set = (key) => (event) => {
    if (key !== 'bot-field') recordFormStart();
    const nextValue = event.target.type === 'checkbox' ? event.target.checked : event.target.value;
    setValues((current) => ({ ...current, [key]: nextValue }));
    setErrors((current) => ({ ...current, [key]: undefined }));
  };

  const setIntent = (event) => {
    recordFormStart();
    setValues((current) => ({
      ...current,
      intent: event.target.value,
      area: '',
      service: '',
      stage: '',
      topic: '',
    }));
    setErrors({});
  };

  async function submit(event) {
    event.preventDefault();
    setSubmitError('');
    const validationErrors = validate(values);
    setErrors(validationErrors);
    if (Object.keys(validationErrors).length) {
      const firstError = Object.keys(validationErrors)[0];
      trackFormValidationError({
        error_count: Object.keys(validationErrors).length,
        first_error_field: firstError,
        form_location: formLocation,
      });
      const firstInvalid = event.currentTarget.querySelector('[aria-invalid="true"], fieldset:has(.bs-field__error)');
      firstInvalid?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return;
    }

    setStatus('submitting');
    const attribution = getAttribution();
    const payload = {
      submission_id: submissionId,
      ...values,
      consent: values.consent ? 'yes' : 'no',
      ...attribution,
    };

    let responseStatus = 'network_error';
    try {
      const response = await fetch('/api/leads', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      responseStatus = response.status;
      if (!response.ok) throw new Error(`Submission failed with status ${response.status}`);
      trackLead({
        content_name: values.intent,
        campaign: attribution.utm_campaign || 'unattributed',
        source: attribution.utm_source || 'direct',
        form_location: formLocation,
      });
      window.sessionStorage.setItem('byte_size_recent_submission', 'yes');
      window.location.assign('/thanks/?submitted=1');
    } catch (error) {
      console.error(error);
      trackFormSubmitError({
        form_location: formLocation,
        response_status: responseStatus,
      });
      setStatus('error');
      setSubmitError('Something went wrong while sending that. Your answers are still here — please try once more.');
    }
  }

  return (
    <Card className={`inquiry-card ${compact ? 'inquiry-card--compact' : ''}`.trim()}>
      <form ref={formRef} name="lead-enquiry" method="POST" action="/api/leads" data-netlify="true" netlify-honeypot="bot-field" onSubmit={submit} noValidate>
        <input type="hidden" name="form-name" value="lead-enquiry" />
        <p className="bot-field" aria-hidden="true">
          <label>Do not fill this out: <input name="bot-field" value={values['bot-field']} onChange={set('bot-field')} tabIndex="-1" autoComplete="off" /></label>
        </p>

        <div className="form-stack">
          <RadioCards name="intent" legend="What brings you here?" options={INTENT_OPTIONS} value={values.intent} onChange={setIntent} required error={errors.intent} />

          {values.intent === 'fix' ? (
            <Field id="area" label="Which area is closest?" className="form-step" required error={errors.area}>
              {(fieldProps) => (
                <Select {...fieldProps} name="area" value={values.area} onChange={set('area')} required>
                  <option value="">Choose one</option>
                  {AREA_OPTIONS.map((option) => <option key={option} value={option}>{option}</option>)}
                </Select>
              )}
            </Field>
          ) : null}

          {values.intent === 'service' ? (
            <Field id="service" label="Which service?" className="form-step" required error={errors.service}>
              {(fieldProps) => (
                <Select {...fieldProps} name="service" value={values.service} onChange={set('service')} required>
                  <option value="">Choose one</option>
                  {services.map((service) => <option key={service.id} value={service.id}>{service.name}</option>)}
                </Select>
              )}
            </Field>
          ) : null}

          {values.intent === 'service' && values.service ? (
            <div className="form-step">
              <RadioCards name="stage" legend="Where are you up to?" options={STAGE_OPTIONS} value={values.stage} onChange={set('stage')} required error={errors.stage} />
            </div>
          ) : null}

          {values.intent === 'general' ? (
            <Field id="topic" label="What is it about?" className="form-step" required error={errors.topic}>
              {(fieldProps) => (
                <Select {...fieldProps} name="topic" value={values.topic} onChange={set('topic')} required>
                  <option value="">Choose one</option>
                  {GENERAL_OPTIONS.map((option) => <option key={option} value={option}>{option}</option>)}
                </Select>
              )}
            </Field>
          ) : null}

          {routeAnswered ? (
            <div className="form-step">
              <RadioCards name="urgency" legend="How urgent is it?" options={URGENCY_OPTIONS} value={values.urgency} onChange={set('urgency')} required error={errors.urgency} />
            </div>
          ) : null}

          {routeAnswered && values.urgency ? (
            <div className="form-grid form-divider form-step">
              <Field id="name" label="Your name" required error={errors.name}>
                {(fieldProps) => <Input {...fieldProps} name="name" value={values.name} onChange={set('name')} autoComplete="name" required />}
              </Field>
              <Field id="reply" label="Email or phone" hint="Whichever you would prefer me to use." required error={errors.reply}>
                {(fieldProps) => <Input {...fieldProps} name="reply" value={values.reply} onChange={set('reply')} required />}
              </Field>
              <Field id="business" label="Business name" hint="Optional" className="full">
                {(fieldProps) => <Input {...fieldProps} name="business" value={values.business} onChange={set('business')} autoComplete="organization" />}
              </Field>
            </div>
          ) : null}

          {routeAnswered && values.urgency && contactAnswered ? (
            <Field id="detail" label={detailCopy.label} hint="Plain English is perfect. You do not need to diagnose it first." className="form-step" required error={errors.detail}>
              {(fieldProps) => <Textarea {...fieldProps} name="detail" rows="7" value={values.detail} onChange={set('detail')} placeholder={detailCopy.placeholder} required />}
            </Field>
          ) : null}

          {values.detail.trim() ? (
            <div className="form-step">
              <label className="bs-check consent-check">
                <input type="checkbox" name="consent" checked={values.consent} onChange={set('consent')} aria-invalid={errors.consent ? 'true' : undefined} required />
                <span className="bs-check__box" aria-hidden="true">✓</span>
                <span>I agree that Byte-Size can use these details to respond to my enquiry. <a href="/privacy/" target="_blank" rel="noreferrer">Privacy details</a>.</span>
              </label>
              {errors.consent ? <span className="bs-field__error" role="alert">{errors.consent}</span> : null}
            </div>
          ) : null}

          {values.detail.trim() ? (
            <div className="form-foot form-step">
              <Button type="submit" iconRight="→" disabled={status === 'submitting'}>{status === 'submitting' ? 'Sending…' : 'Send my problem'}</Button>
              <span className="form-fine">A real person reads every enquiry.</span>
            </div>
          ) : null}
          {submitError ? <div className="form-submit-error" role="alert">{submitError}</div> : null}
        </div>
      </form>
    </Card>
  );
}
